/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/PartnersImageFormModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useRef, useEffect, useState } from "react";
import styles from "./PartnersImageFormModal.module.css";
import { useTranslation } from "react-i18next";
import FileUploadArea from "../ProposalCreateModal/FileUploadArea";
import { CircularProgress } from "@mui/material";
import { createFileObjectFromUrl } from "../../utils/common";
import {
  createPartnersImage,
  updatePartnersImage,
} from "../../logic/partnersImage";
import { useNavigate } from "react-router";

const PartnersImageFormModal = ({
  open,
  onClose,
  currentPartnersImage = null,
  editMode = false,
  onPartnersImageCreated,
  onPartnersImageUpdated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partnersImage, setPartnersImage] = useState(null);
  const [isNewPartnersImageUploaded, setIsNewPartnersImageUploaded] =
    useState(false);

  useEffect(() => {
    if (partnersImage?.imageUrl) {
      setPartnersImage(createFileObjectFromUrl(partnersImage.imageUrl));
    } else {
      setPartnersImage(null);
    }
  }, [partnersImage?.imageUrl]);

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open && dialog) {
      try {
        dialog.showModal();
      } catch (error) {
        logger.error("Error showing modal:", { err: sanitizeError(error) });
      }
    } else if (!open && dialog) {
      try {
        dialog.close();
      } catch (error) {
        logger.error("Error closing modal:", { err: sanitizeError(error) });
      }
    }

    return () => {
      if (dialog) {
        try {
          dialog.removeEventListener("close", handleClose);
        } catch (error) {
          logger.error("Error removing event listener:", {
            err: sanitizeError(error),
          });
        }
      }
    };
  }, [open, onClose]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileValidation(file);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleFileValidation(file);
    }
  };

  const handleFileValidation = async (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      setError(t("pages.buyerCompanyForm.logoFileTypeError"));
      setPartnersImage(null);
      return;
    }

    setError("");
    setPartnersImage(file);
    setIsNewPartnersImageUploaded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let result;

      let partnersImageData = {
        partnersImageFileName: partnersImage?.name,
      };

      if (editMode) {
        result = await updatePartnersImage(
          currentPartnersImage._id,
          partnersImageData,
          navigate,
        );
      } else {
        result = await createPartnersImage(partnersImageData, navigate);
      }

      if (result) {
        logger.info("Partner operation successful:", { data: result });

        if (
          result.presignedUrl &&
          partnersImage &&
          isNewPartnersImageUploaded &&
          partnersImage.name
        ) {
          try {
            const uploadResponse = await fetch(result.presignedUrl, {
              method: "PUT",
              body: partnersImage,
              headers: {
                "Content-Type":
                  partnersImage.type || "application/octet-stream",
              },
            });

            if (!uploadResponse.ok) {
              throw new Error(
                `Failed to upload file: ${uploadResponse.statusText}`,
              );
            }

            logger.info("File uploaded successfully");
          } catch (error) {
            logger.error("Error uploading file:", {
              err: sanitizeError(error),
            });
            setError(t("errors.loading.partnerLogoUploadFailed"));
            return;
          }
        }

        if (editMode && onPartnersImageUpdated) {
          onPartnersImageUpdated(partnersImageData);
        } else if (!editMode && onPartnersImageCreated) {
          onPartnersImageCreated(partnersImageData);
        }

        onClose();
      } else {
        logger.error("Failed to save partner");
        setError(t("errors.loading.partnerSaveFailed"));
      }
    } catch (error) {
      logger.error("Error saving partner:", { err: sanitizeError(error) });
      setError(t("errors.loading.partnerSaveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return isNewPartnersImageUploaded && partnersImage !== null;
  };

  const getModalTitle = () => {
    return editMode
      ? t("components.partnersImageFormModal.editTitle")
      : t("components.partnersImageFormModal.createTitle");
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return editMode
        ? t("components.partnersImageFormModal.saving")
        : t("components.partnersImageFormModal.creating");
    }
    return editMode
      ? t("components.partnersImageFormModal.saveButton")
      : t("components.partnersImageFormModal.createButton");
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.modalOverlay}>
        <dialog ref={dialogRef} className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>{getModalTitle()}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            <form onSubmit={handleSubmit} className={styles.partnerForm}>
              <div className={styles.formSection}>
                <h3>{t("components.partnersImageFormModal.imageLabel")}</h3>
                <FileUploadArea
                  file={partnersImage}
                  isUploading={isUploading}
                  isDragging={isDragging}
                  error={error}
                  onFileChange={handleFileChange}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() =>
                    document.getElementById("partnersImageInput").click()
                  }
                />
                <input
                  id="partnersImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t("components.partnersImageFormModal.cancelButton")}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      <span>{getSubmitButtonText()}</span>
                    </>
                  ) : (
                    getSubmitButtonText()
                  )}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </div>
    </>
  );
};

export default PartnersImageFormModal;
